<!-- Setup Wizard Modal -->
@if (showSetupWizard) {
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
    <div class="bg-white rounded-xl shadow-lg max-w-md w-full p-8 border border-gray-200">
      <h2 class="text-xl font-bold mb-4 text-indigo-700">Welcome! Complete Your Setup</h2>
      <ul class="mb-4 space-y-2">
        @for (step of nextSteps; track step) {
          <li class="flex items-center">
            <span class="inline-block w-3 h-3 rounded-full mr-2"
                  [ngClass]="{
                    'bg-green-400': getStepStatus(step),
                    'bg-gray-300': !getStepStatus(step)
                  }"></span>
            <span>{{step}}</span>
            @if (step === 'Create market area' && !getStepStatus(step)) {
              <button
                class="ml-4 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs"
                (click)="toggleSetupWizard($event)"
              >
                Create
              </button>
            }
          </li>
        }
      </ul>
      <div class="text-sm text-gray-600 mb-4">
        Please complete the above steps to unlock the full dashboard.
      </div>
      <button class="w-full py-2 px-4 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700"
              (click)="dismissSetupWizard()">
        Dismiss
      </button>
    </div>
  </div>
}

<!-- Create Market Area Modal -->
@if (showCreateMarketAreaModal) {
  <div class="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-40">
    <div class="bg-white rounded-xl shadow-lg max-w-3xl w-full p-8 border border-gray-200 min-h-[600px] flex flex-col">
      <h2 class="text-lg font-bold mb-4 text-indigo-700">Create Market Area</h2>
      <div class="flex-1 flex flex-col md:flex-row gap-8 overflow-y-auto">
        <!-- Left: Form -->
        <form [formGroup]="marketAreaForm" class="flex-1 min-w-0">
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Market Area Name <span class="text-red-600">*</span>
            </label>
            <input
              type="text"
              class="w-full border border-gray-300 rounded-lg px-3 py-2"
              formControlName="name"
              required
            />
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Region</label>
            <select
              class="w-full border border-gray-300 rounded-lg px-3 py-2"
              formControlName="region"
            >
              <option value="" disabled selected>No Regions Available</option>
              <!-- No options for now -->
            </select>
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">
              General Managers <span class="text-red-600">*</span>
            </label>
            <lib-type-ahead
              [config]="gmTypeAheadConfig"
              formControlName="gmSearch"
              (selectedMultiple)="onGMMultiSelect($event)">
            </lib-type-ahead>
          </div>
          <div class="mb-4 hidden">
            <label class="block text-sm font-medium text-gray-700 mb-1">Corporate Tenant</label>
            <input
              type="text"
              class="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100"
              formControlName="corporateTenant"
              readonly
            />
          </div>
          <div class="mb-4 hidden">
            <label class="block text-sm font-medium text-gray-700 mb-1">Market Area Manager</label>
            <input
              type="text"
              class="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100"
              formControlName="marketAreaManager"
              readonly
            />
          </div>
          @if (marketAreaFormError) {
            <div class="text-red-600 text-sm mb-2">{{marketAreaFormError}}</div>
          }
        </form>
        <!-- Right: Selected GMs List -->
        <div class="flex-1 min-w-0 border-l border-gray-100 pl-6">
          <div class="font-semibold mb-2 text-sm text-gray-700">Selected GMs:</div>
          @if (selectedGMs.length > 0) {
            <ul class="divide-y divide-gray-200 max-h-80 overflow-y-auto scrollbar-hide scrollbar-hover">
              @for (gm of selectedGMs; track gm.userId) {
                <li class="flex items-center justify-between py-3">
                  <div class="flex flex-col">
                    <span class="font-medium text-sm text-gray-900">{{ gm.name }}</span>
                    <span class="text-xs text-gray-400 mt-1">{{ gm.tenantName }} ({{ gm.tenantAddress }})</span>
                  </div>
                  <button type="button" class="ml-4 text-red-600 hover:underline text-xs font-semibold" (click)="removeGM(gm.userId)">
                    Remove
                  </button>
                </li>
              }
            </ul>
          } @else {
            <div class="text-gray-400 text-sm italic mt-4">No GMs selected yet.</div>
          }
        </div>
      </div>
      <!-- Sticky Action Buttons at the bottom -->
      <div class="sticky bottom-0 bg-white pt-4 flex justify-end gap-2 border-t border-gray-100" style="z-index:10;">
        <button type="button" class="px-4 py-2 rounded-lg bg-gray-200 text-gray-700" (click)="showCreateMarketAreaModal = false">Cancel</button>
        <button
          class="px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700"
          [disabled]="marketAreaFormLoading"
          (click)="submitMarketAreaForm()"
        >
          <span *ngIf="marketAreaFormLoading" class="animate-spin mr-2">⏳</span>
          Create
        </button>
      </div>
    </div>
  </div>
}

<!-- Main Container -->
<div class="min-h-screen bg-gray-50">
  <!-- Sticky Header Section -->
  <header class="sticky top-[60px] z-40 bg-white shadow-sm border-b border-gray-200">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
      <!-- Header Content -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-bold text-gray-900 tracking-tight">Market Area Dashboard</h1>
          <p class="mt-1 text-sm text-gray-500 font-medium">Financial overview and performance metrics for your market area</p>
        </div>
        
        <!-- Date Selection and Refresh -->
        <div class="flex items-center gap-4">
          <!-- Date Picker -->
          <div class="relative">
            <input type="date" 
                   [ngModel]="selectedDate"
                   (ngModelChange)="selectedDate = $event; loadDailyReport()"
                   class="block w-full rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
          </div>

          <!-- Last Update and Refresh -->
          <div class="text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
            <div class="font-medium">Last updated: {{lastUpdateTime | date:'medium'}}</div>
            <div class="text-xs mt-0.5">Next update in: {{timeUntilNextPoll}}</div>
          </div>
          <button (click)="refreshDashboard()" 
                  class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 shadow-sm">
            <svg class="h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Data
          </button>
        </div>
      </div>
    </div>
  </header>

  <!-- Main Content -->
  <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
    <!-- Loading States -->
    @if (loading.shops || loading.dailyReport) {
      <div class="flex justify-center items-center h-64">
        <div class="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-indigo-600"></div>
      </div>
    }

    <!-- Error States -->
    @if (error.shops || error.dailyReport) {
      <div class="mb-6">
        @if (error.shops) {
          <div class="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm text-red-700">{{error.shops}}</p>
              </div>
            </div>
          </div>
        }
        @if (error.dailyReport) {
          <div class="bg-red-50 border-l-4 border-red-400 p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm text-red-700">{{error.dailyReport}}</p>
              </div>
            </div>
          </div>
        }
      </div>
    }

    <!-- Market Area Overview -->
    @if (!loading.shops && !loading.dailyReport) {
      <div class="space-y-6">
        <!-- Market Area Metrics -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="p-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-6">Market Area Performance Overview</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <!-- Total Shops -->
              <div class="bg-gray-50 rounded-lg p-4 border border-gray-100 hover:border-gray-200 transition-colors duration-200">
                <div class="text-sm font-medium text-gray-500">Total Shops</div>
                <div class="mt-1 text-2xl font-semibold text-gray-900">{{getTotalShops()}}</div>
              </div>

              <!-- Daily GP -->
              <div class="bg-gray-50 rounded-lg p-4 border border-gray-100 hover:border-gray-200 transition-colors duration-200">
                <div class="text-sm font-medium text-gray-500">Daily GP</div>
                <div class="mt-1 text-2xl font-semibold text-green-600">${{getTotalDailyRevenue() | number:'1.0-0'}}</div>
              </div>

              <!-- Monthly Budget -->
              <div class="bg-gray-50 rounded-lg p-4 border border-gray-100 hover:border-gray-200 transition-colors duration-200">
                <div class="text-sm font-medium text-gray-500">Monthly Budget</div>
                <div class="mt-1 text-2xl font-semibold text-blue-600">${{getTotalMonthlyRevenue() | number:'1.0-0'}}</div>
              </div>

              <!-- Success Rate -->
              <div class="bg-gray-50 rounded-lg p-4 border border-gray-100 hover:border-gray-200 transition-colors duration-200">
                <div class="text-sm font-medium text-gray-500">Success Rate</div>
                <div class="mt-1 text-2xl font-semibold" [ngClass]="getSuccessRate() >= 80 ? 'text-green-600' : 'text-yellow-600'">
                  {{getSuccessRate() | number:'1.0-1'}}%
                </div>
              </div>
            </div>

            <!-- Additional Metrics -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <!-- Average Daily GP -->
              <div class="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div class="text-sm font-medium text-gray-500">Average Daily GP</div>
                <div class="mt-1 text-xl font-semibold text-indigo-600">${{getAverageDailyGP() | number:'1.0-0'}}</div>
              </div>

              <!-- Total Variance -->
              <div class="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div class="text-sm font-medium text-gray-500">Total Variance</div>
                <div class="mt-1 text-xl font-semibold" [ngClass]="getTotalVariance() >= 0 ? 'text-green-600' : 'text-red-600'">
                  ${{getTotalVariance() | number:'1.0-0'}}
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Shop Performance -->
        <section class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div class="p-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-6">Shop Performance</h2>
            
            <!-- Shop Grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              @for (shop of assignedShops; track shop.shop._id) {
                <div class="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-all duration-200 bg-white">
                  <!-- Shop Header -->
                  <div class="flex justify-between items-start mb-4">
                    <div>
                      <h3 class="text-lg font-medium text-gray-900">{{shop.shop.name}}</h3>
                      <p class="text-sm text-gray-500">{{formatAddress(shop.shop.address)}}</p>
                    </div>
                    <span class="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                      GM: {{shop.generalManager.name}}
                    </span>
                  </div>

                  <!-- Shop Performance Metrics -->
                  @if (getShopPerformance(shop.shop._id); as performance) {
                    <div class="space-y-4">
                      <div class="grid grid-cols-2 gap-3">
                        <!-- Actual Closed -->
                        <div class="bg-gray-50 rounded-lg p-3 border border-gray-100">
                          <div class="text-xs font-medium text-gray-500">Actual Closed</div>
                          <div class="text-sm font-semibold text-gray-900 mt-1">
                            {{performance.actualClosed | number:'1.0-0'}}
                          </div>
                        </div>

                        <!-- Daily GP -->
                        <div class="bg-gray-50 rounded-lg p-3 border border-gray-100">
                          <div class="text-xs font-medium text-gray-500">Daily GP</div>
                          <div class="text-sm font-semibold text-green-600 mt-1">
                            ${{performance.dailyGP | number:'1.0-0'}}
                          </div>
                        </div>

                        <!-- Today's Goal -->
                        <div class="bg-gray-50 rounded-lg p-3 border border-gray-100">
                          <div class="text-xs font-medium text-gray-500">Today's Goal</div>
                          <div class="text-sm font-semibold text-blue-600 mt-1">
                            ${{performance.todayGoal | number:'1.0-0'}}
                          </div>
                        </div>

                        <!-- Variance -->
                        <div class="bg-gray-50 rounded-lg p-3 border border-gray-100">
                          <div class="text-xs font-medium text-gray-500">Variance</div>
                          <div class="text-sm font-semibold mt-1" 
                               [ngClass]="performance.dailyVariance >= 0 ? 'text-green-600' : 'text-red-600'">
                            ${{performance.dailyVariance | number:'1.0-0'}}
                          </div>
                        </div>
                      </div>

                      <!-- Projection Status -->
                      <div class="flex items-center justify-between mt-2">
                        <span class="text-xs font-medium" [ngClass]="performance.metProjection ? 'text-green-600' : 'text-red-600'">
                          {{performance.metProjection ? 'Met Projection' : 'Below Projection'}}
                        </span>
                        <span class="text-xs text-gray-500">
                          Assigned: {{shop.assignedDate | date:'mediumDate'}}
                        </span>
                      </div>
                    </div>
                  } @else {
                    <div class="text-center py-4">
                      <div class="animate-pulse text-gray-400">Loading performance data...</div>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        </section>
      </div>
    }
  </main>
</div> 